import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  useTheme,
  Tabs,
  Tab,
  Stack,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Preview as PreviewIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import VariablePills, { AVAILABLE_VARIABLES } from '../../components/VariablePills';

interface Template {
  _id: string;
  name: string;
  templateType: 'initial' | 'reminder' | 'abandoned';
  subject: string;
  body: string;
  isDefault: boolean;
  variables: string[];
}

const CRMTemplates: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  
  // Form states
  const [name, setName] = useState('');
  const [templateType, setTemplateType] = useState<'initial' | 'reminder' | 'abandoned'>('initial');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [subjectInputRef, setSubjectInputRef] = useState<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const [bodyInputRef, setBodyInputRef] = useState<HTMLInputElement | HTMLTextAreaElement | null>(null);
  
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Check if user is editor (only editors can manage templates)
  const isEditor = user?.role === 'editor';

  // Fetch templates
  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/crm/templates');
      setTemplates(response.data.templates || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  // Filter templates by type
  const filteredTemplates = templates.filter(t => {
    const types = ['initial', 'reminder', 'abandoned'];
    return t.templateType === types[tabValue];
  });

  // Handle create/edit
  const handleSave = async () => {
    if (!name || !subject || !body) {
      setError('All fields are required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (editingTemplate) {
        // Update
        await api.put(`/api/crm/templates/${editingTemplate._id}`, {
          name,
          subject,
          body,
          isDefault
        });
        setSuccess('Template updated successfully!');
      } else {
        // Create
        await api.post('/api/crm/templates', {
          templateType,
          name,
          subject,
          body,
          isDefault
        });
        setSuccess('Template created successfully!');
      }

      setDialogOpen(false);
      resetForm();
      fetchTemplates();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await api.delete(`/api/crm/templates/${templateId}`);
      setSuccess('Template deleted successfully!');
      fetchTemplates();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete template');
    }
  };

  // Handle preview
  const handlePreview = async (template: Template) => {
    try {
      const response = await api.post(`/api/crm/templates/${template._id}/preview`);
      // Show preview in dialog
      setPreviewDialogOpen(true);
    } catch (err) {
      console.error('Failed to preview template:', err);
    }
  };

  // Reset form
  const resetForm = () => {
    setName('');
    setSubject('');
    setBody('');
    setIsDefault(false);
    setEditingTemplate(null);
    setSubjectInputRef(null);
    setBodyInputRef(null);
  };

  // Extract variables from text
  const extractVariables = (text: string): string[] => {
    const regex = /\{\{(\w+)\}\}/g;
    const variables: string[] = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      variables.push(match[1]);
    }
    return variables;
  };

  // Insert variable at cursor position
  const insertVariable = (variableKey: string, field: 'subject' | 'body') => {
    const input = field === 'subject' ? subjectInputRef : bodyInputRef;
    const currentValue = field === 'subject' ? subject : body;
    const setValue = field === 'subject' ? setSubject : setBody;

    if (!input) {
      // If no input ref, just append to the end
      setValue(currentValue + (currentValue ? ' ' : '') + `{{${variableKey}}}`);
      return;
    }

    const variableText = `{{${variableKey}}}`;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    
    // Add space before variable if needed
    const spaceBefore = start > 0 && currentValue[start - 1] !== ' ' ? ' ' : '';
    const spaceAfter = end < currentValue.length && currentValue[end] !== ' ' ? ' ' : '';
    
    const newValue = currentValue.substring(0, start) + spaceBefore + variableText + spaceAfter + currentValue.substring(end);
    setValue(newValue);

    // Set cursor position after inserted variable
    setTimeout(() => {
      const newCursorPos = start + spaceBefore.length + variableText.length + spaceAfter.length;
      input.setSelectionRange(newCursorPos, newCursorPos);
      input.focus();
    }, 0);
  };

  // Get all used variables from both fields
  const getAllUsedVariables = (): string[] => {
    const subjectVars = extractVariables(subject);
    const bodyVars = extractVariables(body);
    return Array.from(new Set([...subjectVars, ...bodyVars]));
  };

  // Open create dialog
  const openCreateDialog = (type: 'initial' | 'reminder' | 'abandoned') => {
    setTemplateType(type);
    resetForm();
    setDialogOpen(true);
  };

  // Open edit dialog
  const openEditDialog = (template: Template) => {
    setEditingTemplate(template);
    setName(template.name);
    setTemplateType(template.templateType);
    setSubject(template.subject);
    setBody(template.body);
    setIsDefault(template.isDefault);
    setDialogOpen(true);
  };

  if (!isEditor) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Only Editors can manage templates</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Email Templates</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            const types = ['initial', 'reminder', 'abandoned'];
            openCreateDialog(types[tabValue] as 'initial' | 'reminder' | 'abandoned');
          }}
        >
          Create Template
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
        <Tab label="Initial" />
        <Tab label="Reminder" />
        <Tab label="Abandoned" />
      </Tabs>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Default</TableCell>
                <TableCell>Variables</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTemplates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No templates found. Create one to get started.
                  </TableCell>
                </TableRow>
              ) : (
                filteredTemplates.map((template) => (
                  <TableRow key={template._id}>
                    <TableCell>{template.name}</TableCell>
                    <TableCell>{template.subject}</TableCell>
                    <TableCell>
                      {template.isDefault && <Chip label="Default" color="primary" size="small" />}
                    </TableCell>
                    <TableCell>
                      {template.variables?.slice(0, 3).map((v) => (
                        <Chip key={v} label={`{{${v}}}`} size="small" sx={{ mr: 0.5 }} />
                      ))}
                      {template.variables?.length > 3 && '...'}
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handlePreview(template)}>
                        <PreviewIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => openEditDialog(template)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(template._id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Template'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Template Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mt: 2 }}
            placeholder="e.g., Welcome Email Template"
          />
          
          {/* Subject Field */}
          <TextField
            fullWidth
            label="Email Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            inputRef={(ref) => setSubjectInputRef(ref)}
            sx={{ mt: 2 }}
            placeholder="e.g., Welcome {{firstName}}!"
            helperText="Type your subject and use variable pills below to personalize it"
          />

          {/* Shared Variable Pills Section - Between Subject and Body */}
          <Box sx={{ mt: 3 }}>
            <VariablePills
              onInsert={(key) => {
                // Determine which field has focus, or default to body
                const activeElement = document.activeElement;
                if (activeElement === subjectInputRef || (subjectInputRef && activeElement === subjectInputRef)) {
                  insertVariable(key, 'subject');
                } else {
                  insertVariable(key, 'body');
                }
              }}
              usedVariables={getAllUsedVariables()}
            />
          </Box>

          {/* Body Field */}
          <TextField
            fullWidth
            label="Email Body"
            multiline
            rows={12}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            inputRef={(ref) => setBodyInputRef(ref)}
            sx={{ mt: 3 }}
            placeholder="Dear {{firstName}},&#10;&#10;We noticed you were looking into {{companyName}}..."
            helperText="Write your email message and use variable pills above to personalize it"
          />

          <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                style={{ marginRight: 8, cursor: 'pointer' }}
              />
              <Typography variant="body2">
                Set as default template for this type
              </Typography>
            </label>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 4 }}>
              When set as default, this template will be pre-selected when sending emails
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || !name || !subject || !body}>
            {saving ? <CircularProgress size={20} /> : 'Save Template'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CRMTemplates;

